import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  styled,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  Autocomplete,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Menu,
  Card,
  CardContent,
  FormControlLabel,
  Switch
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Public as PublicIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  QrCode2 as QrCode2Icon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Upload as UploadIcon,
  FilePresent as FilePresentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { tr } from 'date-fns/locale';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { getBranches as getBranchesAdapter, createBranch as createBranchSql, updateBranch as updateBranchSql } from '../../services/migration/branchDataService';
import { getAssignmentsByBranch, assignManagerToBranch, removeAssignment } from '../../services/migration/branchPersonnelAssignmentDataService';
import { updatePersonnel } from '../../services/migration/personnelDataService';
import { useAuth } from '../../contexts/AuthContext';
import BranchQRModal from './BranchQRModal';
import ManagerPermissionsModal from './ManagerPermissionsModal';
import BranchCompanyDetailsModal from './BranchCompanyDetailsModal';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { mainApi } from '../../config/apiClient';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 180px)', // Header ve diğer öğeler için alan bırakıyoruz
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  '@media (max-width: 1499px)': {
    height: 'auto',
    overflowY: 'visible',
  },
}));

// Sekme içerik bileşeni tipi
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// TabPanel bileşeni
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`branch-tabpanel-${index}`}
      aria-labelledby={`branch-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `branch-tab-${index}`,
    'aria-controls': `branch-tabpanel-${index}`,
  };
}

const Branches: React.FC = () => {
  const { t, i18n } = useTranslation('branches');
  const currentLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  // Auth ve şirket bilgileri
  const { userDetails } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  
  // State değişkenleri
  const [loading, setLoading] = useState(true);
  const [savingBranch, setSavingBranch] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [exporting, setExporting] = useState(false);
  
  // QR Modal için state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<{id: string, name: string} | null>(null);
  
  // Yönetici atama modalı için state
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [availablePersonnel, setAvailablePersonnel] = useState<any[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');
  const [filteredPersonnel, setFilteredPersonnel] = useState<any[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [savingManagers, setSavingManagers] = useState(false);
  
  // Yöneticileri görüntüleme modalı için state
  const [managersModalOpen, setManagersModalOpen] = useState(false);
  const [branchManagers, setBranchManagers] = useState<any[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [deletingManagerId, setDeletingManagerId] = useState<string | null>(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

  // Yönetici izinleri modalı için state
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);

  // API Senkronizasyon state
  const [syncing, setSyncing] = useState(false);

  // Şube filtresi (Autocomplete)
  const [branchFilter, setBranchFilter] = useState<{ id: string; name: string } | null>(null);

  // Statü filtresi (Autocomplete) — id: 'all' | 'branch' | 'franchise'
  const [statusFilter, setStatusFilter] = useState<{ id: 'all' | 'branch' | 'franchise'; name: string } | null>(null);

  // Filtrelenmiş ve alfabetik sıralanmış şubeler
  const displayedBranches = useMemo(() => {
    let result = [...branches];
    if (branchFilter) {
      result = result.filter(b => b.id === branchFilter.id);
    }
    if (statusFilter && statusFilter.id !== 'all') {
      const wantFranchise = statusFilter.id === 'franchise';
      result = result.filter(b => Boolean(b.isFranchise) === wantFranchise);
    }
    return result.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
  }, [branches, branchFilter, statusFilter]);

  // Şube düzenleme için state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<any>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    openingDate: Date | null;
    address: string;
    district: string;
    city: string;
    postalCode: string;
    country: string;
    phone: string;
    email: string;
    website: string;
    isFranchise: boolean;
  }>({
    name: '',
    openingDate: null,
    address: '',
    district: '',
    city: '',
    postalCode: '',
    country: 'Türkiye',
    phone: '',
    email: '',
    website: '',
    isFranchise: false
  });
  const [editFormErrors, setEditFormErrors] = useState({
    name: false,
    address: false,
    phone: false
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Şube silme için state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState<{id: string, name: string} | null>(null);
  const [deletingBranch, setDeletingBranch] = useState(false);
  const [branchUsageInfo, setBranchUsageInfo] = useState<{
    canDelete: boolean;
    usageDetails: string[];
    checking: boolean;
  }>({
    canDelete: true,
    usageDetails: [],
    checking: false
  });

  // Firma Bilgileri Modal state
  const [companyDetailsModalOpen, setCompanyDetailsModalOpen] = useState(false);
  const [companyDetailsBranch, setCompanyDetailsBranch] = useState<{id: string, name: string} | null>(null);

  // Menü state (masaüstü satır menüsü + mobil)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBranchForMenu, setSelectedBranchForMenu] = useState<{id: string, name: string} | null>(null);
  const [anchorElRow, setAnchorElRow] = useState<Record<string, HTMLElement | null>>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, branch: {id: string, name: string}) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedBranchForMenu(branch);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBranchForMenu(null);
  };

  const handleOpenRowMenu = (branch: any, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorElRow(prev => ({ ...prev, [branch.id]: e.currentTarget }));
  };

  const handleCloseRowMenu = (branch: any) => {
    setAnchorElRow(prev => ({ ...prev, [branch.id]: null }));
  };

  const handleCopyAddress = (branch: any) => {
    const fullAddress = [branch.address, branch.district, branch.city, branch.country !== 'Türkiye' ? branch.country : null].filter(Boolean).join(', ');
    navigator.clipboard.writeText(fullAddress).then(() => {
      setSnackbarSeverity('success');
      setSnackbarMessage(t('messages.addressCopied'));
      setSnackbarOpen(true);
    });
  };

  // AccountingDataTable kolon tanımları
  const branchColumns = useMemo<DataTableColumn<any>[]>(() => [
    {
      id: 'rowNumber',
      label: '#',
      width: '50px',
      render: (_row: any, index: number) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{index + 1}</Box>
      ),
    },
    {
      id: 'name',
      label: t('table.columns.branchName'),
      sortable: true,
      field: 'name' as any,
      render: (row: any) => (
        <Typography variant="body2" fontWeight="bold">{row.name}</Typography>
      ),
    },
    {
      id: 'createdAt',
      label: t('table.columns.createdAt'),
      sortable: true,
      width: '140px',
      render: (row: any) => (
        <Typography variant="body2" color="text.secondary">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString(currentLocale) : '-'}
        </Typography>
      ),
    },
    {
      id: 'address',
      label: t('table.columns.address'),
      width: '250px',
      cellSx: { maxWidth: '250px' },
      render: (row: any) => {
        const fullAddress = [row.address, row.district, row.city, row.country !== 'Türkiye' ? row.country : null].filter(Boolean).join(', ');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={fullAddress} arrow placement="top">
              <Typography variant="body2" noWrap sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fullAddress || '-'}
              </Typography>
            </Tooltip>
            {fullAddress && (
              <Tooltip title={t('actions.copyAddress')}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopyAddress(row); }} sx={{ p: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: 'phone',
      label: t('table.columns.phone'),
      sortable: true,
      render: (row: any) => (
        <Typography variant="body2">{row.phone || '-'}</Typography>
      ),
    },
    {
      id: 'email',
      label: t('table.columns.email'),
      sortable: true,
      render: (row: any) => (
        <Typography variant="body2" color="text.secondary">{row.email || '-'}</Typography>
      ),
    },
    {
      id: 'status',
      label: t('table.columns.status'),
      sortable: true,
      width: '120px',
      // ⚠️ Sıralamanın çalışması için `field` zorunlu — yoksa DataTable `row.status` arar (yok),
      // ve Chip render'ı sıralama yapamaz. `isFranchise` boolean alanını sortField olarak kullanıyoruz:
      // false (Şube) < true (Franchise) — asc'te şubeler önce, desc'te franchise'lar önce.
      field: 'isFranchise' as any,
      render: (row: any) => (
        <Chip
          label={row.isFranchise ? t('status.franchise') : t('status.branch')}
          size="small"
          color={row.isFranchise ? 'warning' : 'primary'}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      ),
    },
    {
      // Firma bilgileri girilmis mi? — Firebase'de companies/{companyId}/branches/{id}/companyInfo
      // alt nesnesinden geliyor. firmName veya firmTaxNumber doluysa "girilmis" sayilir.
      id: 'companyInfo',
      label: t('table.columns.companyInfo'),
      align: 'center',
      width: '130px',
      render: (row: any) => {
        const ci = row?.companyInfo || {};
        const hasInfo = Boolean(
          (ci.firmName && String(ci.firmName).trim()) ||
          (ci.firmTaxNumber && String(ci.firmTaxNumber).trim())
        );
        return hasInfo ? (
          <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main' }} />
        ) : (
          <Box component="span" sx={{ color: 'text.disabled', fontWeight: 600 }}>—</Box>
        );
      },
    },
    {
      id: 'actions',
      label: t('table.columns.actions'),
      align: 'right',
      width: '80px',
      render: (row: any) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={(e) => handleOpenRowMenu(row, e)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => handleCloseRowMenu(row)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenCompanyDetailsModal({ id: row.id, name: row.name }); }}>
              <ListItemIcon><BusinessIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>{t('actions.companyDetails')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenEditModal(row); }}>
              <ListItemIcon><EditIcon fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText>{t('actions.edit')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenQRModal({ id: row.id, name: row.name }); }}>
              <ListItemIcon><QrCode2Icon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>{t('actions.qrCode')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenAdminModal({ id: row.id, name: row.name }); }}>
              <ListItemIcon><PersonAddIcon fontSize="small" color="secondary" /></ListItemIcon>
              <ListItemText>{t('actions.assignManager')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenPermissionsModal({ id: row.id, name: row.name }); }}>
              <ListItemIcon><SecurityIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText>{t('actions.managerPermissions')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenManagersModal({ id: row.id, name: row.name }); }}>
              <ListItemIcon><GroupIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>{t('actions.managers')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
  ], [anchorElRow, currentLocale, t]);

  // Toplu şube ekleme modalı için state
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState<{
    total: number;
    success: number;
    failed: number;
    results: Array<{
      row: number;
      name: string;
      status: 'success' | 'error';
      message: string;
    }>;
  }>({
    total: 0,
    success: 0,
    failed: 0,
    results: []
  });

  // Form state değişkenleri
  const [branchForm, setBranchForm] = useState<{
    name: string;
    openingDate: Date | null;
    address: string;
    district: string;
    city: string;
    postalCode: string;
    country: string;
    phone: string;
    email: string;
    website: string;
    isFranchise: boolean;
  }>({
    name: '',
    openingDate: null,
    address: '',
    district: '',
    city: '',
    postalCode: '',
    country: 'Türkiye',
    phone: '',
    email: '',
    website: '',
    isFranchise: false
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({
    name: false,
    address: false,
    phone: false
  });

  // Component mount olduğunda şirket bilgilerini ve şubeleri yükle
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        setLoading(true);
        
        // Giriş yapmış kullanıcıyı kontrol et
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setLoading(false);
          return;
        }
        
        // Kullanıcı verilerini al ve şirket ID'sini bul
        const userData = await firebaseDataService.getUserData(uid);
        if (!userData) {
          setLoading(false);
          return;
        }
        const companyId = userData.companyId || null;
        setCompanyId(companyId);
        
        if (!companyId) {
          setLoading(false);
          return;
        }
        
        // Subeleri SQL-once oku (branchDataService): HybridFallback'te SQL bos/hata olursa
        // Firebase'e duser ve SQL'e tohumlar. Yazma artik yalniz SQL oldugu icin liste de SQL-once.
        const serviceBranches = await getBranchesAdapter(companyId);
        const sortedBranches = [...serviceBranches].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
        setBranches(sortedBranches);
        setLoading(false);
      } catch (error) {
        console.error('Şube verilerini yüklerken hata:', error);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('messages.loadError'));
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    
    loadCompanyData();
  }, []);

  // Şubeyi API'den sil
  const deleteBranchFromAPI = async (firebaseId: string, companyIdToSync: string) => {
    try {
      const token = await ownAuthSession.getAccessToken();
      if (!token) {
        console.warn('⚠️ Token alınamadı, silme senkronizasyonu atlandı');
        return;
      }

      const response = await mainApi.delete(
        `/api/branch/sync/${firebaseId}?companyId=${encodeURIComponent(companyIdToSync)}&hardDelete=true`
      );

      if (response.data.success) {
        console.log(`✅ Şube API'den silindi: ${firebaseId}`);
      }
    } catch (error) {
      console.error('❌ Şube API silme hatası:', error);
    }
  };

  // Tab değişimi işleyici
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Modal açma/kapama işleyicileri
  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    // Form değerlerini sıfırla
    setBranchForm({
      name: '',
      openingDate: null,
      address: '',
      district: '',
      city: '',
      postalCode: '',
      country: 'Türkiye',
      phone: '',
      email: '',
      website: '',
      isFranchise: false
    });
    // Form hatalarını sıfırla
    setFormErrors({
      name: false,
      address: false,
      phone: false
    });
  };

  // Snackbar kapatma işleyicisi
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Form değişim işleyicisi
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBranchForm((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Zorunlu alanlar için hata kontrolü
    if (['name', 'address', 'phone'].includes(name)) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: value.trim() === ''
      }));
    }
  };

  // Select değişim işleyicisi
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setBranchForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Tarih değişim işleyicisi
  const handleDateChange = (date: Date | null) => {
    setBranchForm((prev) => ({
      ...prev,
      openingDate: date
    }));
  };

  // Form gönderme işleyicisi
  const handleSubmit = async () => {
    // Zorunlu alanları kontrol et
    const newErrors = {
      name: branchForm.name.trim() === '',
      address: branchForm.address.trim() === '',
      phone: branchForm.phone.trim() === ''
    };
    
    setFormErrors(newErrors);
    
    // Eğer hata yoksa kaydet
    if (!newErrors.name && !newErrors.address && !newErrors.phone) {
      try {
        if (!companyId) {
          throw new Error('Şirket bilgisi bulunamadı');
        }
        
        setSavingBranch(true);
        
        // Sube verilerini hazirla - duz yapi (gruplandirma olmadan)
        const branchData = {
          createdAt: Date.now(),
          name: branchForm.name,
          openingDate: branchForm.openingDate ? branchForm.openingDate.toISOString() : null,
          address: branchForm.address,
          district: branchForm.district || '',
          city: branchForm.city || '',
          postalCode: branchForm.postalCode || '',
          country: branchForm.country,
          phone: branchForm.phone,
          email: branchForm.email || '',
          website: branchForm.website || '',
          isFranchise: branchForm.isFranchise
        };

        // SADECE SQL'e yaz (tek-kaynak). Donen id kanonik sube id'sidir (= FirebaseId).
        const newBranchId = await createBranchSql({
          name: branchData.name,
          address: branchData.address,
          district: branchData.district,
          city: branchData.city,
          postalCode: branchData.postalCode,
          country: branchData.country,
          phone: branchData.phone,
          email: branchData.email,
          website: branchData.website,
          openingDate: branchData.openingDate,
          isFranchise: branchData.isFranchise,
        });

        // Listeyi guncelle
        const newBranchWithId = { id: newBranchId, ...branchData };
        setBranches(prev => [newBranchWithId, ...prev]);
        
        // Modal'ı kapat
        handleCloseAddModal();
        
        // Bilgi mesajı göster
        setSnackbarSeverity('success');
        setSnackbarMessage(t('messages.addSuccess'));
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Şube eklerken hata:', error);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('messages.addError'));
        setSnackbarOpen(true);
      } finally {
        setSavingBranch(false);
      }
    }
  };

  // Geçerli sekmeye göre başlık belirleme
  const getTabTitle = () => {
    switch (tabValue) {
      case 0:
        return t('tabs.list');
      default:
        return t('page.management');
    }
  };

  // Excel'e veri aktarma işlevi
  const exportToExcel = async () => {
    setExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Şubeler');

      // Başlık satırı
      worksheet.columns = [
        { header: 'Şube Adı', key: 'name', width: 20 },
        { header: 'Statü', key: 'status', width: 12 },
        { header: 'Açılış Tarihi (YYYY-MM-DD)', key: 'openingDate', width: 25 },
        { header: 'Adres', key: 'address', width: 40 },
        { header: 'İlçe', key: 'district', width: 15 },
        { header: 'Şehir/İl', key: 'city', width: 15 },
        { header: 'Posta Kodu', key: 'postalCode', width: 12 },
        { header: 'Ülke', key: 'country', width: 12 },
        { header: 'Telefon', key: 'phone', width: 15 },
        { header: 'E-posta', key: 'email', width: 25 },
        { header: 'Web Sitesi', key: 'website', width: 25 }
      ];

      // Mevcut şubeleri ekle (alfabetik sırada)
      [...branches].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).forEach(branch => {
        worksheet.addRow({
          name: branch.name,
          status: branch.isFranchise ? t('status.franchise') : t('status.branch'),
          openingDate: branch.openingDate ? new Date(branch.openingDate).toISOString().split('T')[0] : '',
          address: branch.address,
          district: branch.district || '',
          city: branch.city || '',
          postalCode: branch.postalCode || '',
          country: branch.country || 'Türkiye',
          phone: branch.phone,
          email: branch.email || '',
          website: branch.website || ''
        });
      });

      // Başlık satırını stil ile biçimlendir
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '366092' }
        };
        cell.font = {
          color: { argb: 'FFFFFF' },
          bold: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `subeler-${new Date().toISOString().split('T')[0]}.xlsx`);

      setSnackbarMessage('Excel dosyası başarıyla indirildi!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Excel export hatası:', error);
      setSnackbarMessage('Excel dosyası oluşturulurken bir hata oluştu.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setExporting(false);
    }
  };

  // Excel template indirme
  const downloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Şube Template');

      // Başlık satırı
      worksheet.columns = [
        { header: 'Şube Adı*', key: 'name', width: 20 },
        { header: 'Açılış Tarihi (YYYY-MM-DD)', key: 'openingDate', width: 25 },
        { header: 'Adres*', key: 'address', width: 40 },
        { header: 'İlçe', key: 'district', width: 15 },
        { header: 'Şehir/İl', key: 'city', width: 15 },
        { header: 'Posta Kodu', key: 'postalCode', width: 12 },
        { header: 'Ülke', key: 'country', width: 12 },
        { header: 'Telefon*', key: 'phone', width: 15 },
        { header: 'E-posta', key: 'email', width: 25 },
        { header: 'Web Sitesi', key: 'website', width: 25 }
      ];

      // Örnek veri satırları
      worksheet.addRows([
        {
          name: 'Merkez Şubesi',
          openingDate: '2025-01-15',
          address: 'Atatürk Caddesi No:123 Merkez',
          district: 'Merkez',
          city: 'İstanbul',
          postalCode: '34000',
          country: 'Türkiye',
          phone: '+90 212 123 45 67',
          email: 'merkez@example.com',
          website: 'https://www.example.com'
        },
        {
          name: 'Kadıköy Şubesi',
          openingDate: '2025-02-01',
          address: 'Bağdat Caddesi No:456 Kadıköy',
          district: 'Kadıköy',
          city: 'İstanbul',
          postalCode: '34710',
          country: 'Türkiye',
          phone: '+90 216 987 65 43',
          email: 'kadikoy@example.com',
          website: ''
        }
      ]);

      // Başlık satırını stil ile biçimlendir
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '366092' }
        };
        cell.font = {
          color: { argb: 'FFFFFF' },
          bold: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Zorunlu alanları kırmızı yap
      worksheet.getRow(1).getCell(1).font = { color: { argb: 'FFFF0000' }, bold: true }; // Şube Adı
      worksheet.getRow(1).getCell(3).font = { color: { argb: 'FFFF0000' }, bold: true }; // Adres  
      worksheet.getRow(1).getCell(8).font = { color: { argb: 'FFFF0000' }, bold: true }; // Telefon

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'sube-template.xlsx');

      setSnackbarMessage('Template dosyası başarıyla indirildi!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Template indirme hatası:', error);
      setSnackbarMessage('Template dosyası oluşturulurken bir hata oluştu.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // QR Modal açma işlevi
  const handleOpenQRModal = (branch: {id: string, name: string}) => {
    setSelectedBranch(branch);
    setQrModalOpen(true);
  };

  const handleCloseQRModal = () => {
    setQrModalOpen(false);
  };
  
  // Yönetici atama modalı açma işlevi
  const handleOpenAdminModal = (branch: {id: string, name: string}) => {
    setSelectedBranch(branch);
    setAdminModalOpen(true);
    fetchAvailablePersonnel(branch);
  };

  const handleCloseAdminModal = () => {
    setAdminModalOpen(false);
    setSelectedPersonnel([]);
    setFilterText('');
  };
  
  // Yöneticileri görüntüleme modalını açma işlevi
  const handleOpenManagersModal = (branch: {id: string, name: string}) => {
    setSelectedBranch(branch);
    setManagersModalOpen(true);
    fetchBranchManagers(branch.id);
  };

  const handleCloseManagersModal = () => {
    setManagersModalOpen(false);
    setBranchManagers([]);
  };

  // Yönetici izinleri modalını açma işlevi
  const handleOpenPermissionsModal = (branch: {id: string, name: string}) => {
    setSelectedBranch(branch);
    setPermissionsModalOpen(true);
  };

  const handleClosePermissionsModal = () => {
    setPermissionsModalOpen(false);
  };

  const handleOpenCompanyDetailsModal = (branch: {id: string, name: string}) => {
    setCompanyDetailsBranch(branch);
    setCompanyDetailsModalOpen(true);
  };

  const handleCloseCompanyDetailsModal = (updated?: boolean) => {
    setCompanyDetailsModalOpen(false);
    setCompanyDetailsBranch(null);
  };

  // Şube düzenleme modalını açma işlevi
  const handleOpenEditModal = (branch: any) => {
    setEditingBranch(branch);
    setEditForm({
      name: branch.name || '',
      openingDate: branch.openingDate ? new Date(branch.openingDate) : null,
      address: branch.address || '',
      district: branch.district || '',
      city: branch.city || '',
      postalCode: branch.postalCode || '',
      country: branch.country || 'Türkiye',
      phone: branch.phone || '',
      email: branch.email || '',
      website: branch.website || '',
      isFranchise: branch.isFranchise || false
    });
    setEditFormErrors({
      name: false,
      address: false,
      phone: false
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingBranch(null);
    setEditForm({
      name: '',
      openingDate: null,
      address: '',
      district: '',
      city: '',
      postalCode: '',
      country: 'Türkiye',
      phone: '',
      email: '',
      website: '',
      isFranchise: false
    });
    setEditFormErrors({
      name: false,
      address: false,
      phone: false
    });
  };

  // Düzenleme formu değişim işleyicisi
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Zorunlu alanlar için hata kontrolü
    if (['name', 'address', 'phone'].includes(name)) {
      setEditFormErrors((prev) => ({
        ...prev,
        [name]: value.trim() === ''
      }));
    }
  };

  // Düzenleme select değişim işleyicisi
  const handleEditSelectChange = (e: any) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Düzenleme tarih değişim işleyicisi
  const handleEditDateChange = (date: Date | null) => {
    setEditForm((prev) => ({
      ...prev,
      openingDate: date
    }));
  };

  // Şube güncelleme işlemi
  const handleEditSubmit = async () => {
    // Zorunlu alanları kontrol et
    const newErrors = {
      name: editForm.name.trim() === '',
      address: editForm.address.trim() === '',
      phone: editForm.phone.trim() === ''
    };
    
    setEditFormErrors(newErrors);
    
    // Eğer hata yoksa güncelle
    if (!newErrors.name && !newErrors.address && !newErrors.phone) {
      try {
        if (!companyId || !editingBranch) {
          throw new Error('Şirket veya şube bilgisi bulunamadı');
        }
        
        setSavingEdit(true);
        
        // Guncellenecek verileri hazirla
        const updatedData = {
          name: editForm.name,
          openingDate: editForm.openingDate ? editForm.openingDate.toISOString() : null,
          address: editForm.address,
          district: editForm.district || '',
          city: editForm.city || '',
          postalCode: editForm.postalCode || '',
          country: editForm.country,
          phone: editForm.phone,
          email: editForm.email || '',
          website: editForm.website || '',
          isFranchise: editForm.isFranchise,
          updatedAt: Date.now()
        };

        // SADECE SQL'e yaz (tek-kaynak). Firm* alanlarina dokunmaz (ayri uctan yonetilir).
        await updateBranchSql(editingBranch.id, {
          name: updatedData.name,
          address: updatedData.address,
          district: updatedData.district,
          city: updatedData.city,
          postalCode: updatedData.postalCode,
          country: updatedData.country,
          phone: updatedData.phone,
          email: updatedData.email,
          website: updatedData.website,
          openingDate: updatedData.openingDate,
          isFranchise: updatedData.isFranchise,
        });

        // Listeyi guncelle
        setBranches(prev => prev.map(branch => 
          branch.id === editingBranch.id 
            ? { ...branch, ...updatedData }
            : branch
        ));
        
        // Modal'ı kapat
        handleCloseEditModal();
        
        // Bilgi mesajı göster
        setSnackbarSeverity('success');
        setSnackbarMessage('Şube başarıyla güncellendi!');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Şube güncellerken hata:', error);
        setSnackbarSeverity('error');
        setSnackbarMessage('Şube güncellenirken bir hata oluştu.');
        setSnackbarOpen(true);
      } finally {
        setSavingEdit(false);
      }
    }
  };

  // Toplu ekleme modal işleyicileri
  const handleOpenBulkUploadModal = () => {
    setBulkUploadModalOpen(true);
  };

  const handleCloseBulkUploadModal = () => {
    setBulkUploadModalOpen(false);
    setSelectedFile(null);
    setBulkResults({
      total: 0,
      success: 0,
      failed: 0,
      results: []
    });
    setBulkProgress(0);
  };

  // Dosya seçimi işleyici
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Dosya türü kontrolü
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(file.type)) {
        setSnackbarMessage('Sadece Excel dosyaları (.xlsx, .xls) kabul edilmektedir.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbarMessage('Dosya boyutu 5MB\'dan küçük olmalıdır.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      setSelectedFile(file);
    }
  };

  // Excel dosyasını işle ve şubeleri ekle
  const processBulkUpload = async () => {
    if (!selectedFile || !companyId) return;

    setUploadingBulk(true);
    setBulkProgress(0);
    const results: Array<{
      row: number;
      name: string;
      status: 'success' | 'error';
      message: string;
    }> = [];

    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await selectedFile.arrayBuffer());
      const worksheet = workbook.getWorksheet(1);

      if (!worksheet) {
        throw new Error('Excel dosyasında çalışma sayfası bulunamadı');
      }

      const rows: any[] = [];
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1) { // İlk satır başlık
          const values = row.values as any[];
          rows.push({
            rowNumber,
            name: values[1]?.toString().trim() || '',
            openingDate: values[2]?.toString().trim() || '',
            address: values[3]?.toString().trim() || '',
            district: values[4]?.toString().trim() || '',
            city: values[5]?.toString().trim() || '',
            postalCode: values[6]?.toString().trim() || '',
            country: values[7]?.toString().trim() || 'Türkiye',
            phone: values[8]?.toString().trim() || '',
            email: values[9]?.toString().trim() || '',
            website: values[10]?.toString().trim() || ''
          });
        }
      });

      if (rows.length === 0) {
        throw new Error('Excel dosyasında işlenecek veri bulunamadı');
      }

      if (rows.length > 100) {
        throw new Error('Tek seferde en fazla 100 şube eklenebilir');
      }

      // Mevcut şube isimleri kontrolü için
      const existingBranchNames = branches.map(b => b.name.toLowerCase());

      for (let i = 0; i < rows.length; i++) {
        const rowData = rows[i];
        const progress = Math.round(((i + 1) / rows.length) * 100);
        setBulkProgress(progress);

        try {
          // Zorunlu alan kontrolü
          if (!rowData.name) {
            results.push({
              row: rowData.rowNumber,
              name: rowData.name || `Satır ${rowData.rowNumber}`,
              status: 'error',
              message: 'Şube adı zorunludur'
            });
            continue;
          }

          if (!rowData.address) {
            results.push({
              row: rowData.rowNumber,
              name: rowData.name,
              status: 'error',
              message: 'Adres bilgisi zorunludur'
            });
            continue;
          }

          if (!rowData.phone) {
            results.push({
              row: rowData.rowNumber,
              name: rowData.name,
              status: 'error',
              message: 'Telefon numarası zorunludur'
            });
            continue;
          }

          // Duplicate kontrol
          if (existingBranchNames.includes(rowData.name.toLowerCase())) {
            results.push({
              row: rowData.rowNumber,
              name: rowData.name,
              status: 'error',
              message: 'Bu isimde bir şube zaten mevcut'
            });
            continue;
          }

          // Tarih formatı kontrolü
          let openingDate: Date | null = null;
          if (rowData.openingDate) {
            const dateStr = rowData.openingDate.toString();
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              openingDate = new Date(dateStr);
              if (isNaN(openingDate.getTime())) {
                openingDate = null;
              }
            } else {
              // Excel tarih formatı kontrolü
              const excelDate = new Date(dateStr);
              if (!isNaN(excelDate.getTime())) {
                openingDate = excelDate;
              }
            }
          }

          // Sube verilerini hazirla
          const branchData = {
            createdAt: Date.now(),
            name: rowData.name,
            openingDate: openingDate ? openingDate.toISOString() : null,
            address: rowData.address,
            district: rowData.district,
            city: rowData.city,
            postalCode: rowData.postalCode,
            country: rowData.country,
            phone: rowData.phone,
            email: rowData.email,
            website: rowData.website
          };

          // SADECE SQL'e yaz (tek-kaynak).
          await createBranchSql({
            name: branchData.name,
            address: branchData.address,
            district: branchData.district,
            city: branchData.city,
            postalCode: branchData.postalCode,
            country: branchData.country,
            phone: branchData.phone,
            email: branchData.email,
            website: branchData.website,
            openingDate: branchData.openingDate,
          });

          // Basariyla eklenen subeyi mevcut listeye ekle
          existingBranchNames.push(rowData.name.toLowerCase());

          results.push({
            row: rowData.rowNumber,
            name: rowData.name,
            status: 'success',
            message: 'Başarıyla eklendi'
          });

          // Kısa bir gecikme ekle (Firebase rate limiting için)
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (error: any) {
          console.error(`Satır ${rowData.rowNumber} işlenirken hata:`, error);
          results.push({
            row: rowData.rowNumber,
            name: rowData.name || `Satır ${rowData.rowNumber}`,
            status: 'error',
            message: error.message || 'Bilinmeyen hata oluştu'
          });
        }
      }

      const successCount = results.filter(r => r.status === 'success').length;
      const failedCount = results.filter(r => r.status === 'error').length;

      setBulkResults({
        total: results.length,
        success: successCount,
        failed: failedCount,
        results
      });

      if (successCount > 0) {
        setSnackbarMessage(`${successCount} şube başarıyla eklendi${failedCount > 0 ? `, ${failedCount} şube eklenemedi` : ''}`);
        setSnackbarSeverity(failedCount > 0 ? 'warning' : 'success');
      } else {
        setSnackbarMessage('Hiçbir şube eklenemedi. Lütfen Excel dosyanızı kontrol edin.');
        setSnackbarSeverity('error');
      }
      setSnackbarOpen(true);

    } catch (error: any) {
      console.error('Toplu yükleme hatası:', error);
      setSnackbarMessage(error.message || 'Toplu yükleme sırasında bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploadingBulk(false);
      setBulkProgress(100);
    }
  };
  
  // Şube yöneticilerini getirme
  const fetchBranchManagers = async (branchId: string) => {
    if (!companyId) return;
    
    setLoadingManagers(true);
    let combinedManagers: any[] = [];

    try {
      // 1. Birincil yöneticileri SQL'den getir (eski Node /api/manager/list yerine).
      //    Birincil yönetici = role === 'manager' && branchesId === branchId && !isDeleted.
      //    useCache=false: atama updatePersonnel ile SQL'e yazılır ama FirebaseDataService
      //    önbelleği temizlenmez — bayat okumada yeni yönetici listede görünmüyordu.
      const allPersonnel = await firebaseDataService.getPersonnel(companyId, undefined, false);
      const personnelArray = Array.isArray(allPersonnel) ? allPersonnel : (allPersonnel ? [allPersonnel] : []);
      combinedManagers = personnelArray
        .filter((p: any) => p && p.role === 'manager' && p.branchesId === branchId && !p.isDeleted)
        .map((p: any) => ({
          id: p.id,
          name: p.name || 'İsimsiz Personel',
          email: p.email || '',
          phone: p.phone || '',
        }));

      // 2. SQL üzerinden ek şube atamalarını getir (eski RTDB branch_personnel_assignments dugumu kaldirildi).
      const assignments = await getAssignmentsByBranch(branchId);

      if (assignments.length > 0) {
        const assignmentPromises: Promise<({ id: string; name: string; email: string; phone: string; } | null)>[] = [];
        assignments.forEach((assignment) => {
          if (assignment.userId) {
            // Her bir userId için personel detaylarını SQL'den çekme promise'i oluştur
            const personnelDetailPromise = async () => {
              try {
                const [uData, personnelData] = await Promise.all([
                  firebaseDataService.getUserData(assignment.userId),
                  firebaseDataService.getPersonnel(companyId, assignment.userId, false),
                ]);

                const personnel = personnelData as any;

                let name = 'İsimsiz Personel';
                let email = '';
                let phone = '';

                // Personel kaydindan isim/telefon (SQL).
                let hasPersonnelName = false;
                if (personnel) {
                  const pName = (personnel.name || `${personnel.firstName || ''} ${personnel.lastName || ''}`).trim();
                  if (pName) {
                    name = pName;
                    hasPersonnelName = true;
                  }
                  phone = personnel.phone || phone;
                }

                // Kullanici kaydindan email + isim fallback (SQL).
                if (uData) {
                  email = uData.email || email;
                  const uName = `${uData.firstName || ''} ${uData.lastName || ''}`.trim();
                  name = !hasPersonnelName && uName ? uName : name;
                }

                return {
                  id: assignment.userId, // API manager listesiyle uyumlu ID alanı
                  name,
                  email,
                  phone,
                };
              } catch (err) {
                console.error(`Personel detayı çekilirken hata (userId: ${assignment.userId}):`, err);
                return null; // Hata durumunda null dön, Promise.all devam etsin
              }
            };
            assignmentPromises.push(personnelDetailPromise());
          }
        });

        const firebaseManagersWithDetails = (await Promise.all(assignmentPromises)).filter(p => p !== null) as { id: string; name: string; email: string; phone: string; }[];
        combinedManagers = [...combinedManagers, ...firebaseManagersWithDetails];
      }

      // 3. Tüm yöneticileri ID'ye göre birleştir ve tekilleştir
      const uniqueManagersMap = new Map();
      combinedManagers.forEach(manager => {
        if (manager && manager.id) { // manager ve manager.id null/undefined değilse
            uniqueManagersMap.set(manager.id, manager);
        }
      });
      const finalUniqueManagers = Array.from(uniqueManagersMap.values());

      setBranchManagers(finalUniqueManagers);

    } catch (error) {
      console.error('Şube yöneticileri yüklenirken genel hata:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Şube yöneticileri yüklenirken bir hata oluştu.');
      setSnackbarOpen(true);
      setBranchManagers([]); // Hata durumunda listeyi boşalt
    } finally {
      setLoadingManagers(false);
    }
  };
  
  // Yönetici silme onay modalını aç
  const handleConfirmDeleteManager = (managerId: string) => {
    setDeletingManagerId(managerId);
    setConfirmDeleteModal(true);
  };
  
  // Yönetici silme onay modalını kapat
  const handleCloseConfirmDelete = () => {
    setConfirmDeleteModal(false);
    setDeletingManagerId(null);
  };
  
  // Yönetici silme işlemi
  const handleRemoveManager = async () => {
    if (!deletingManagerId || !companyId || !selectedBranch) return;
    
    try {
      // Yöneticiyi SQL üzerinden kaldır (eski Node /api/manager/remove yerine).
      // 1. Bu şubenin birincil yöneticisiyse rolünü düşür (employee) ve şube bağını temizle.
      // 2. Ek şube ataması (BranchPersonnelAssignments) varsa onu da kaldır.
      // useCache=false: bayat rol/şube bilgisiyle yanlış karar verilmesin.
      const allPersonnel = await firebaseDataService.getPersonnel(companyId, undefined, false);
      const personnelArray = Array.isArray(allPersonnel) ? allPersonnel : (allPersonnel ? [allPersonnel] : []);
      // PersonnelData tipi role/branchesId alanlarını bildirmez ama SQL projeksiyonu döndürür
      const target = personnelArray.find((p: any) => p && p.id === deletingManagerId) as any;

      let changed = false;

      // Sadece bu şubenin birincil yöneticisi ise rol düşür (başka şubenin yöneticisini etkileme).
      if (target && target.role === 'manager' && target.branchesId === selectedBranch.id) {
        await updatePersonnel(deletingManagerId, { role: 'employee', branchesId: '' });
        changed = true;
      }

      // Ek şube atamasını (varsa) SQL'den kaldır.
      try {
        const removeResult = await removeAssignment(selectedBranch.id, deletingManagerId);
        if (removeResult.removedCount > 0) changed = true;
      } catch (assignErr) {
        console.warn('Ek şube ataması kaldırılırken hata (kritik değil):', assignErr);
      }

      // Başarı mesajı göster
      setSnackbarSeverity('success');
      setSnackbarMessage(changed ? 'Yönetici başarıyla kaldırıldı.' : 'Personel zaten bu şubede yönetici değildi.');
      setSnackbarOpen(true);
      
      // Yönetici listesini güncelle
      fetchBranchManagers(selectedBranch.id);
      
    } catch (error) {
      console.error('Yönetici silme hatası:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Yönetici silinirken bir hata oluştu.');
      setSnackbarOpen(true);
    } finally {
      setConfirmDeleteModal(false);
      setDeletingManagerId(null);
    }
  };
  
  // Görev atanmamış personelleri getirme
  const fetchAvailablePersonnel = async (currentBranchForModal: {id: string, name: string} | null) => {
    if (!companyId || !currentBranchForModal) return;
    
    setLoadingPersonnel(true);
    
    try {
      // useCache=false KRİTİK: rol/birincil şube bilgisi bayatsa, zaten yönetici olan
      // kişi "yönetici değil" sanılıp Senaryo 1 çalışır ve birincil şubesi TAŞINIR
      // (ek atama yazılacağına). İki şubeye atama bu yüzden bozuluyordu.
      const personnelResult = await firebaseDataService.getPersonnel(companyId, undefined, false);
      const personnelList = Array.isArray(personnelResult) ? personnelResult : (personnelResult ? [personnelResult] : []);

      const availablePersonnelListWithDetails = personnelList
        .map((person: any) => {
          const currentUserRole = person.role ?? null;
          const currentPrimaryBranchId = person.branchesId ?? null;

          const isNotDeleted = !person.isDeleted;
          const isNotPrimaryManagerOfCurrentBranch = !(currentUserRole === 'manager' && currentPrimaryBranchId === currentBranchForModal.id);

          if (isNotDeleted && isNotPrimaryManagerOfCurrentBranch) {
            return {
              id: person.id,
              name: person.name || 'İsimsiz Personel',
              email: person.email || '',
              phone: person.phone || '',
              currentUserRole,
              currentPrimaryBranchId
            };
          }
          return null;
        })
        .filter(p => p !== null) as any[];

      setAvailablePersonnel(availablePersonnelListWithDetails);
      setFilteredPersonnel(availablePersonnelListWithDetails);
    } catch (error) {
      console.error('Personel verileri yüklenirken hata:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Personel verileri yüklenirken bir hata oluştu.');
      setSnackbarOpen(true);
    } finally {
      setLoadingPersonnel(false);
    }
  };
  
  // Personel checkbox değişimi
  const handlePersonnelToggle = (personnelId: string) => {
    const currentIndex = selectedPersonnel.indexOf(personnelId);
    const newChecked = [...selectedPersonnel];

    if (currentIndex === -1) {
      newChecked.push(personnelId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedPersonnel(newChecked);
  };
  
  // Personel filtre işlemi
  useEffect(() => {
    if (!filterText) {
      setFilteredPersonnel(availablePersonnel);
      return;
    }
    
    const filtered = availablePersonnel.filter(person => 
      person.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (person.email && person.email.toLowerCase().includes(filterText.toLowerCase())) ||
      (person.phone && person.phone.includes(filterText))
    );
    
    setFilteredPersonnel(filtered);
  }, [filterText, availablePersonnel]);
  
  // Yönetici kaydetme işlemi
  const handleSaveAdmins = async () => {
    if (!selectedBranch || !companyId || selectedPersonnel.length === 0) return;
    
    const currentSelectedBranch = selectedBranch; // TypeScript'e null olmadığını belirtmek için

    try {
      setSavingManagers(true);
      const token = await ownAuthSession.getAccessToken();
      if (!token) {
        setSnackbarSeverity('error');
        setSnackbarMessage('Oturum bilgisi alınamadı. Lütfen tekrar giriş yapın.');
        setSnackbarOpen(true);
        setSavingManagers(false);
        return;
      }

      const results = await Promise.all(selectedPersonnel.map(async (personnelId) => {
        const personnelData = availablePersonnel.find(p => p.id === personnelId);
        if (!personnelData) {
          return { success: false, personnelId, name: 'Bilinmeyen Personel', message: 'Personel bilgisi bulunamadı.' };
        }

        const { currentUserRole, currentPrimaryBranchId, name: personnelName } = personnelData;

        // Senaryo 1: Personel henüz birincil yönetici değil VEYA birincil şubesi yok -> SQL ile ata
        if (currentUserRole !== 'manager' || !currentPrimaryBranchId) {
          try {
            // Eski Node /api/manager/assign yerine: personeli SQL'de birincil yönetici yap.
            await updatePersonnel(personnelId, { role: 'manager', branchesId: currentSelectedBranch.id });
            return { success: true, personnelId, name: personnelName, message: `${personnelName} ${currentSelectedBranch.name} şubesine birincil yönetici olarak atandı.` };
          } catch (apiError: any) {
            console.error(`SQL ile ${personnelId} (${personnelName}) atama hatası:`, apiError);
            return { success: false, personnelId, name: personnelName, message: apiError.response?.data?.message || apiError.message || 'Yönetici atama sırasında bir hata oluştu.' };
          }
        }
        // Senaryo 2: Personel zaten birincil yönetici ve FARKLI bir şubeye atanıyor -> branch_personnel_assignments'a yaz
        else if (currentUserRole === 'manager' && currentPrimaryBranchId && currentPrimaryBranchId !== currentSelectedBranch.id) {
          try {
            // companyId'nin null veya undefined olmadığından emin olun
            if (!companyId) {
              return { success: false, personnelId, name: personnelName, message: 'Şirket ID bulunamadı, ek şube ataması yapılamıyor.' };
            }

            // Ek sube atamasi artik SQL'e yazilir (eski RTDB branch_personnel_assignments dugumu kaldirildi).
            const result = await assignManagerToBranch(currentSelectedBranch.id, personnelId);
            if (result.alreadyExists) {
              return { success: true, personnelId, name: personnelName, message: `${personnelName} zaten ek olarak ${currentSelectedBranch.name} şubesine atanmış.` };
            }
            return { success: true, personnelId, name: personnelName, message: `${personnelName} ek olarak ${currentSelectedBranch.name} şubesine yönetici olarak atandı (veritabanı).` };
          } catch (dbError: any) {
            console.error(`Veritabanına ${personnelId} (${personnelName}) atama hatası:`, dbError);
            return { success: false, personnelId, name: personnelName, message: dbError.message || 'Veritabanına ek şube ataması sırasında bir hata oluştu.' };
          }
        }
        // Senaryo 3: Personel zaten bu şubenin birincil yöneticisi
        else if (currentPrimaryBranchId === currentSelectedBranch.id) {
             return { success: true, personnelId, name: personnelName, message: `${personnelName} zaten ${currentSelectedBranch.name} şubesinin birincil yöneticisi.` };
        }
        
        return { success: false, personnelId, name: personnelName, message: 'Geçersiz atama durumu veya bilinmeyen bir hata oluştu.' }; // Her ihtimale karşı bir dönüş
      }));

      // Sonuçları işle
      const successes = results.filter(r => r.success);
      const failures = results.filter(r => !r.success);
      let successMessages = successes.map(s => s.message).join('\n');
      let failureMessages = failures.map(f => `${f.name || f.personnelId}: ${f.message}`).join('\n');

      if (successes.length > 0 && failures.length > 0) {
        setSnackbarSeverity('warning');
        setSnackbarMessage(`Atama İşlemi Kısmen Başarılı:\nBaşarılı: ${successMessages}\nBaşarısız: ${failureMessages}`);
      } else if (successes.length > 0) {
        setSnackbarSeverity('success');
        setSnackbarMessage(`Yönetici Atama Başarılı:\n${successMessages}`);
      } else if (failures.length > 0) {
        setSnackbarSeverity('error');
        setSnackbarMessage(`Yönetici Atama Başarısız:\n${failureMessages}`);
      } // Eğer hiç işlem yapılmadıysa (örneğin tümü zaten atanmışsa ve success olmayan bir mesajla döndüyse) bir mesaj gösterilmeyebilir.
      
      setSnackbarOpen(true);
      
      // Sadece en az bir başarılı atama varsa modalı kapat ve listeyi yenile
      const refreshNeeded = results.some(r => r.success && (r.message.includes('API ile') || r.message.includes('(veritabanı)') || r.message.includes('ek olarak')));
      
      if (refreshNeeded) {      
        handleCloseAdminModal();
        fetchAvailablePersonnel(selectedBranch); 
      }
      
    } catch (error) {
      console.error('Yönetici atama genel hatası:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('messages.managerAssignError'));
      setSnackbarOpen(true);
    } finally {
      setSavingManagers(false);
    }
  };

  return (
    <Paper sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 1 : 2 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        mb: 2,
        gap: isMobile ? 2 : 0
      }}>
        {/* Üst Satır: Başlık + Yeni Şube Ekle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} component="h1">
            {isMobile ? t('page.title') : getTabTitle()}
          </Typography>
          {isMobile && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
            >
              {t('buttons.newBranch')}
            </Button>
          )}
        </Box>
        
        {/* Butonlar */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={isMobile ? 'outlined' : 'contained'}
            color="info"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            disabled={exporting}
          >
            {exporting ? t('buttons.downloading') : (isMobile ? t('buttons.excelShort') : t('buttons.downloadExcel'))}
          </Button>
          {!isMobile && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
            >
              {t('buttons.addNewBranch')}
            </Button>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: isMobile ? 2 : 3 }} />
      
      {!isMobile && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label={t('tabs.ariaLabel')}>
            <Tab label={t('tabs.list')} {...a11yProps(0)} />
          </Tabs>
        </Box>
      )}
      
      {/* Masaüstü */}
      {!isMobile ? (
        <>
          <Box sx={{ mb: 2, mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '0 0 320px', maxWidth: 320 }}>
              <Autocomplete
                options={[{ id: '', name: t('filters.allBranches') }, ...[...branches].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(b => ({ id: b.id, name: b.name }))]}
                getOptionLabel={(opt) => opt.name}
                value={branchFilter ?? { id: '', name: t('filters.allBranches') }}
                onChange={(_, newValue) => setBranchFilter(newValue?.id ? newValue : null)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('filters.branchFilter')} placeholder={t('filters.selectBranch')} size="small" />
                )}
              />
            </Box>
            <Box sx={{ flex: '0 0 240px', maxWidth: 240 }}>
              <Autocomplete
                options={[
                  { id: 'all' as const, name: t('filters.allStatuses') },
                  { id: 'branch' as const, name: t('status.branch') },
                  { id: 'franchise' as const, name: t('status.franchise') },
                ]}
                getOptionLabel={(opt) => opt.name}
                value={statusFilter ?? { id: 'all' as const, name: t('filters.allStatuses') }}
                onChange={(_, newValue) => setStatusFilter(newValue && newValue.id !== 'all' ? newValue : null)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('filters.statusFilter')} placeholder={t('filters.selectStatus')} size="small" />
                )}
              />
            </Box>
          </Box>
          <AccountingDataTable<any>
            columns={branchColumns}
            rows={displayedBranches}
            getRowId={(row) => row.id}
            loading={loading}
            defaultRowsPerPage={100}
            defaultSortField="name"
            defaultSortDirection="asc"
            emptyMessage={t('table.empty')}
            emptyIcon={<BusinessIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
            ariaLabel={t('table.ariaLabel')}
            columnResizeStorageKey="branches"
          />
        </>
      ) : (
          /* Mobil: Direkt içerik, ScrollableContent yok */
          <Box sx={{ pt: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : branches.length > 0 ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Autocomplete
                    options={[{ id: '', name: t('filters.allBranches') }, ...[...branches].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(b => ({ id: b.id, name: b.name }))]}
                    getOptionLabel={(opt) => opt.name}
                    value={branchFilter ?? { id: '', name: t('filters.allBranches') }}
                    onChange={(_, newValue) => setBranchFilter(newValue?.id ? newValue : null)}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('filters.branchFilter')} placeholder={t('filters.selectBranch')} size="small" fullWidth />
                    )}
                  />
                  <Autocomplete
                    options={[
                      { id: 'all' as const, name: t('filters.allStatuses') },
                      { id: 'branch' as const, name: t('status.branch') },
                      { id: 'franchise' as const, name: t('status.franchise') },
                    ]}
                    getOptionLabel={(opt) => opt.name}
                    value={statusFilter ?? { id: 'all' as const, name: t('filters.allStatuses') }}
                    onChange={(_, newValue) => setStatusFilter(newValue && newValue.id !== 'all' ? newValue : null)}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('filters.statusFilter')} placeholder={t('filters.selectStatus')} size="small" fullWidth />
                    )}
                  />
                </Box>
                <Grid container spacing={2}>
                  {displayedBranches.map((branch) => (
                    <Grid item xs={12} key={branch.id}>
                      <Card sx={{ borderLeft: 4, borderColor: 'primary.main' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {/* Üst Satır: Şube Adı + Tarih + 3 Nokta */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <BusinessIcon fontSize="small" color="primary" />
                                  {branch.name}
                                </Typography>
                                <Chip
                                  label={branch.isFranchise ? t('status.franchise') : t('status.branch')}
                                  size="small"
                                  color={branch.isFranchise ? 'warning' : 'primary'}
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                                📅 {new Date(branch.createdAt).toLocaleDateString(currentLocale)}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, {id: branch.id, name: branch.name})}>
                              <MoreVertIcon />
                            </IconButton>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          {/* Adres Bölümü */}
                          <Box sx={{ mb: 1.5 }}>
                            <Typography component="div" variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <LocationIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                              <Box>
                                {branch.address}
                                {(branch.district || branch.city) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0 }}>
                                    {branch.district}{branch.district && branch.city && ', '}{branch.city}
                                  </Typography>
                                )}
                                {branch.country && branch.country !== 'Türkiye' && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {branch.country}
                                  </Typography>
                                )}
                              </Box>
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          {/* İletişim Bölümü */}
                          <Box>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              {branch.phone}
                            </Typography>
                            {branch.email && (
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <EmailIcon fontSize="small" color="action" />
                                {branch.email}
                              </Typography>
                            )}
                            {branch.website && (
                              <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PublicIcon fontSize="small" color="primary" />
                                {branch.website}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {displayedBranches.length === 0 && (branchFilter || statusFilter) && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">{t('filters.noMatchingBranches')}</Typography>
                  </Box>
                )}

                {/* Mobil 3 Nokta Menüsü */}
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        handleOpenCompanyDetailsModal(selectedBranchForMenu);
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.companyDetails')}</ListItemText>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        const fullBranch = branches.find(b => b.id === selectedBranchForMenu.id);
                        if (fullBranch) {
                          handleOpenEditModal(fullBranch);
                        }
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.edit')}</ListItemText>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        handleOpenQRModal(selectedBranchForMenu);
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <QrCode2Icon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.qrCode')}</ListItemText>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        handleOpenAdminModal(selectedBranchForMenu);
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <PersonAddIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.assignManager')}</ListItemText>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        handleOpenPermissionsModal(selectedBranchForMenu);
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <SecurityIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.managerPermissions')}</ListItemText>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedBranchForMenu) {
                        handleOpenManagersModal(selectedBranchForMenu);
                      }
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText>{t('actions.managers')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                <BusinessIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('empty.noBranches')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('empty.addFirstBranchHint')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddModal}
                >
                  {t('buttons.addNewBranch')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      
      {/* Şube Ekleme Modal */}
      <Dialog 
        open={openAddModal} 
        onClose={handleCloseAddModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('modals.addBranch.title')}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseAddModal} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Temel Bilgiler */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label={t('modals.shared.fields.branchName')}
                fullWidth
                required
                value={branchForm.name}
                onChange={handleFormChange}
                error={formErrors.name}
                helperText={formErrors.name ? t('modals.shared.validation.branchNameRequired') : ''}
                InputProps={{
                  startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <DatePicker
                  label={t('modals.shared.fields.openingDate')}
                  value={branchForm.openingDate}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: <CalendarIcon color="action" sx={{ mr: 1 }} />,
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={branchForm.isFranchise}
                    onChange={(e) => setBranchForm((prev) => ({ ...prev, isFranchise: e.target.checked }))}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{t('modals.shared.fields.franchiseBranch')}</Typography>
                    <Chip
                      label={branchForm.isFranchise ? t('status.franchise') : t('status.branch')}
                      size="small"
                      color={branchForm.isFranchise ? 'warning' : 'primary'}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                }
              />
            </Grid>
            
            {/* Konum Bilgileri */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.locationInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="address"
                label={t('table.columns.address')}
                fullWidth
                required
                multiline
                rows={3}
                value={branchForm.address}
                onChange={handleFormChange}
                error={formErrors.address}
                helperText={formErrors.address ? t('modals.shared.validation.addressRequired') : ''}
                InputProps={{
                  startAdornment: <LocationIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="district"
                label={t('modals.shared.fields.district')}
                fullWidth
                value={branchForm.district}
                onChange={handleFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="city"
                label={t('modals.shared.fields.city')}
                fullWidth
                value={branchForm.city}
                onChange={handleFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="postalCode"
                label={t('modals.shared.fields.postalCode')}
                fullWidth
                value={branchForm.postalCode}
                onChange={handleFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="country-label">{t('modals.shared.fields.country')}</InputLabel>
                <Select
                  labelId="country-label"
                  name="country"
                  value={branchForm.country}
                  label={t('modals.shared.fields.country')}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Türkiye">Türkiye</MenuItem>
                  <MenuItem value="Almanya">Almanya</MenuItem>
                  <MenuItem value="İngiltere">İngiltere</MenuItem>
                  <MenuItem value="Fransa">Fransa</MenuItem>
                  <MenuItem value="İtalya">İtalya</MenuItem>
                  <MenuItem value="İspanya">İspanya</MenuItem>
                  <MenuItem value="Hollanda">Hollanda</MenuItem>
                  <MenuItem value="Belçika">Belçika</MenuItem>
                  <MenuItem value="Diğer">Diğer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* İletişim Bilgileri */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.contactInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="phone"
                label={t('modals.shared.fields.phoneNumber')}
                fullWidth
                required
                value={branchForm.phone}
                onChange={handleFormChange}
                error={formErrors.phone}
                helperText={formErrors.phone ? t('modals.shared.validation.phoneRequired') : ''}
                InputProps={{
                  startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="email"
                label={t('modals.shared.fields.emailAddress')}
                fullWidth
                type="email"
                value={branchForm.email}
                onChange={handleFormChange}
                InputProps={{
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="website"
                label={t('companyDetails.fields.firmWebsite')}
                fullWidth
                value={branchForm.website}
                onChange={handleFormChange}
                placeholder={t('companyDetails.fields.websitePlaceholder')}
                InputProps={{
                  startAdornment: <PublicIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="inherit" disabled={savingBranch}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleOpenBulkUploadModal}
            variant="outlined"
            color="secondary"
            disabled={savingBranch}
            startIcon={<UploadIcon />}
          >
            {t('modals.addBranch.bulkAdd')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={savingBranch}
            startIcon={savingBranch ? <CircularProgress size={24} color="inherit" /> : null}
          >
            {savingBranch ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Şube QR Modal */}
      {selectedBranch && (
        <BranchQRModal
          open={qrModalOpen}
          onClose={handleCloseQRModal}
          branchId={selectedBranch.id}
          branchName={selectedBranch.name}
        />
      )}

      {/* Şube Düzenleme Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('modals.editBranch.title')}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseEditModal} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Temel Bilgiler */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label={t('modals.shared.fields.branchName')}
                fullWidth
                required
                value={editForm.name}
                onChange={handleEditFormChange}
                error={editFormErrors.name}
                helperText={editFormErrors.name ? t('modals.shared.validation.branchNameRequired') : ''}
                InputProps={{
                  startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <DatePicker
                  label={t('modals.shared.fields.openingDate')}
                  value={editForm.openingDate}
                  onChange={handleEditDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: <CalendarIcon color="action" sx={{ mr: 1 }} />,
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isFranchise}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, isFranchise: e.target.checked }))}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{t('modals.shared.fields.franchiseBranch')}</Typography>
                    <Chip
                      label={editForm.isFranchise ? t('status.franchise') : t('status.branch')}
                      size="small"
                      color={editForm.isFranchise ? 'warning' : 'primary'}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                }
              />
            </Grid>
            
            {/* Konum Bilgileri */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.locationInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="address"
                label={t('table.columns.address')}
                fullWidth
                required
                multiline
                rows={3}
                value={editForm.address}
                onChange={handleEditFormChange}
                error={editFormErrors.address}
                helperText={editFormErrors.address ? t('modals.shared.validation.addressRequired') : ''}
                InputProps={{
                  startAdornment: <LocationIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="district"
                label={t('modals.shared.fields.district')}
                fullWidth
                value={editForm.district}
                onChange={handleEditFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="city"
                label={t('modals.shared.fields.city')}
                fullWidth
                value={editForm.city}
                onChange={handleEditFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="postalCode"
                label={t('modals.shared.fields.postalCode')}
                fullWidth
                value={editForm.postalCode}
                onChange={handleEditFormChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="edit-country-label">{t('modals.shared.fields.country')}</InputLabel>
                <Select
                  labelId="edit-country-label"
                  name="country"
                  value={editForm.country}
                  label={t('modals.shared.fields.country')}
                  onChange={handleEditSelectChange}
                >
                  <MenuItem value="Türkiye">Türkiye</MenuItem>
                  <MenuItem value="Almanya">Almanya</MenuItem>
                  <MenuItem value="İngiltere">İngiltere</MenuItem>
                  <MenuItem value="Fransa">Fransa</MenuItem>
                  <MenuItem value="İtalya">İtalya</MenuItem>
                  <MenuItem value="İspanya">İspanya</MenuItem>
                  <MenuItem value="Hollanda">Hollanda</MenuItem>
                  <MenuItem value="Belçika">Belçika</MenuItem>
                  <MenuItem value="Diğer">Diğer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* İletişim Bilgileri */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                {t('modals.shared.contactInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="phone"
                label={t('modals.shared.fields.phoneNumber')}
                fullWidth
                required
                value={editForm.phone}
                onChange={handleEditFormChange}
                error={editFormErrors.phone}
                helperText={editFormErrors.phone ? t('modals.shared.validation.phoneRequired') : ''}
                InputProps={{
                  startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="email"
                label={t('modals.shared.fields.emailAddress')}
                fullWidth
                type="email"
                value={editForm.email}
                onChange={handleEditFormChange}
                InputProps={{
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="website"
                label={t('companyDetails.fields.firmWebsite')}
                fullWidth
                value={editForm.website}
                onChange={handleEditFormChange}
                placeholder={t('companyDetails.fields.websitePlaceholder')}
                InputProps={{
                  startAdornment: <PublicIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="inherit" disabled={savingEdit}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
            disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={24} color="inherit" /> : <EditIcon />}
          >
            {savingEdit ? t('modals.editBranch.updating') : t('modals.editBranch.update')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Toplu Şube Yükleme Modal */}
      <Dialog
        open={bulkUploadModalOpen}
        onClose={handleCloseBulkUploadModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('modals.bulkUpload.title')}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseBulkUploadModal} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {!uploadingBulk && bulkResults.total === 0 ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                {t('modals.bulkUpload.description')}
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  <strong>{t('modals.bulkUpload.notesTitle')}</strong><br />
                  {t('modals.bulkUpload.notes')}
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<FilePresentIcon />}
                    onClick={downloadTemplate}
                    sx={{ py: 2 }}
                  >
                    {t('modals.bulkUpload.downloadTemplate')}
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {t('modals.bulkUpload.templateHint')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="excel-file-input"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="excel-file-input">
                    <Button
                      variant="contained"
                      component="span"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{ py: 2 }}
                    >
                      {t('modals.bulkUpload.selectExcel')}
                    </Button>
                  </label>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {t('modals.bulkUpload.fileFormatHint')}
                  </Typography>
                </Grid>
              </Grid>

              {selectedFile && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {t('modals.bulkUpload.selectedFile')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilePresentIcon color="primary" />
                    <Typography variant="body2">{selectedFile.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : uploadingBulk ? (
            <Box>
              <Typography variant="h6" gutterBottom align="center">
                {t('modals.bulkUpload.processing')}
              </Typography>
              <Box sx={{ mt: 3, mb: 2 }}>
                <LinearProgress variant="determinate" value={bulkProgress} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              <Typography variant="body2" align="center" color="text.secondary">
                {t('modals.bulkUpload.progress', { value: bulkProgress })}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('modals.bulkUpload.keepOpenWarning')}
              </Alert>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('modals.bulkUpload.results')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h4">{bulkResults.total}</Typography>
                    <Typography variant="body2">{t('modals.bulkUpload.summary.total')}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h4">{bulkResults.success}</Typography>
                    <Typography variant="body2">{t('modals.bulkUpload.summary.success')}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="h4">{bulkResults.failed}</Typography>
                    <Typography variant="body2">{t('modals.bulkUpload.summary.failed')}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('modals.bulkUpload.table.row')}</TableCell>
                      <TableCell>{t('modals.bulkUpload.table.branchName')}</TableCell>
                      <TableCell>{t('modals.bulkUpload.table.status')}</TableCell>
                      <TableCell>{t('modals.bulkUpload.table.message')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkResults.results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.row}</TableCell>
                        <TableCell>{result.name}</TableCell>
                        <TableCell>
                          {result.status === 'success' ? (
                            <Chip 
                              icon={<CheckCircleIcon />} 
                              label={t('modals.bulkUpload.summary.success')} 
                              color="success" 
                              size="small" 
                            />
                          ) : (
                            <Chip 
                              icon={<ErrorIcon />} 
                              label={t('modals.bulkUpload.error')} 
                              color="error" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell>{result.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleCloseBulkUploadModal} 
            color="inherit"
            disabled={uploadingBulk}
          >
            {bulkResults.total > 0 ? t('common.close') : t('common.cancel')}
          </Button>
          {selectedFile && !uploadingBulk && bulkResults.total === 0 && (
            <Button
              onClick={processBulkUpload}
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
            >
              {t('modals.bulkUpload.startUpload')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Şube Yöneticileri Görüntüleme Modal */}
      <Dialog
        open={managersModalOpen}
        onClose={handleCloseManagersModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedBranch
                ? t('modals.managers.titleForBranch', { branchName: selectedBranch.name })
                : t('modals.managers.title')}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseManagersModal} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingManagers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : branchManagers.length > 0 ? (
            <List>
              {branchManagers.map((manager) => (
                <ListItem
                  key={manager.id}
                  secondaryAction={
                    <Tooltip title={t('modals.managers.removeManager')}>
                      <IconButton 
                        edge="end" 
                        aria-label={t('actions.delete')} 
                        color="error"
                        onClick={() => handleConfirmDeleteManager(manager.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {manager.name.charAt(0)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={manager.name}
                    secondary={
                      <Box>
                        {manager.email && (
                          <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                            <EmailIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                            {manager.email}
                          </Typography>
                        )}
                        {manager.phone && (
                          <Typography variant="body2" component="span">
                            <PhoneIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                            {manager.phone}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <GroupIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" align="center">
                {t('modals.managers.empty')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                sx={{ mt: 2 }}
                onClick={() => {
                  handleCloseManagersModal();
                  if (selectedBranch) {
                    handleOpenAdminModal(selectedBranch);
                  }
                }}
              >
                {t('actions.assignManager')}
              </Button>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleCloseManagersModal}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Şube Yöneticisi Atama Modal */}
      <Dialog
        open={adminModalOpen}
        onClose={handleCloseAdminModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedBranch
                ? t('modals.assignManager.titleForBranch', { branchName: selectedBranch.name })
                : t('modals.assignManager.title')}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseAdminModal} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingPersonnel ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('modals.assignManager.description')}
              </Typography>
              
              <Autocomplete
                freeSolo
                options={[]}
                inputValue={filterText}
                onInputChange={(_, newValue) => setFilterText(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('modals.assignManager.searchPersonnel')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <SearchIcon color="action" sx={{ ml: 1, mr: -0.5 }} />
                      )
                    }}
                  />
                )}
              />
              
              <Box sx={{ mt: 2, maxHeight: '50vh', overflowY: 'auto' }}>
                {filteredPersonnel.length > 0 ? (
                  <List dense>
                    {filteredPersonnel.map((person) => (
                      <ListItemButton 
                        key={person.id}
                        onClick={() => handlePersonnelToggle(person.id)}
                        sx={{ 
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedPersonnel.indexOf(person.id) !== -1}
                            tabIndex={-1}
                            disableRipple
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={person.name}
                          secondary={
                            <Box>
                              {person.email && (
                                <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                                  <EmailIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  {person.email}
                                </Typography>
                              )}
                              {person.phone && (
                                <Typography variant="body2" component="span">
                                  <PhoneIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  {person.phone}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                    {filterText ? t('modals.assignManager.noPersonnelForSearch') : t('modals.assignManager.noAvailablePersonnel')}
                  </Typography>
                )}
              </Box>
              
              {selectedPersonnel.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {t('modals.assignManager.selectedPersonnel', { count: selectedPersonnel.length })}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedPersonnel.map(id => {
                      const person = availablePersonnel.find(p => p.id === id);
                      return person ? (
                        <Chip 
                          key={id}
                          label={person.name}
                          variant="outlined"
                          color="primary"
                          size="small"
                          onDelete={() => handlePersonnelToggle(id)}
                          sx={{ m: 0.5 }}
                        />
                      ) : null;
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCloseAdminModal}
            disabled={savingManagers}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveAdmins}
            disabled={selectedPersonnel.length === 0 || savingManagers}
            startIcon={savingManagers ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {savingManagers ? t('common.saving') : t('modals.assignManager.assignAsManager')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Yönetici Silme Onay Modalı */}
      <Dialog
        open={confirmDeleteModal}
        onClose={handleCloseConfirmDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('modals.removeManager.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('modals.removeManager.confirm')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('modals.removeManager.description')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDelete}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleRemoveManager} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t('modals.removeManager.remove')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Yönetici İzinleri Modal */}
      {selectedBranch && companyId && (
        <ManagerPermissionsModal
          open={permissionsModalOpen}
          onClose={handleClosePermissionsModal}
          branchId={selectedBranch.id}
          branchName={selectedBranch.name}
          companyId={companyId}
        />
      )}
      
      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Firma Bilgileri Modalı */}
      <BranchCompanyDetailsModal
        open={companyDetailsModalOpen}
        onClose={handleCloseCompanyDetailsModal}
        companyId={companyId}
        branchId={companyDetailsBranch?.id || null}
        branchName={companyDetailsBranch?.name || ''}
      />
    </Paper>
  );
};

export default Branches; 